import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_55a9f4cb-4c33-47bd-86a7-80fe678bbbdb/artifacts/wavtipa0_PROKSY%20RESEARCH%20%282%29.png";

export default function Footer() {
    return (
        <footer
            className="relative border-t border-white/5 bg-[#04091A]"
            data-testid="main-footer"
        >
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40 pointer-events-none" />
            <div className="pr-container relative py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
                    <div className="md:col-span-1">
                        <Link
                            to="/"
                            className="flex items-center gap-3"
                            data-testid="footer-logo-link"
                        >
                            <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/10 bg-black/30">
                                <img
                                    src={LOGO_URL}
                                    alt="Proksy Research"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="leading-tight">
                                <p className="font-outfit font-bold text-white">
                                    PROKSY
                                </p>
                                <p className="font-outfit text-[10px] tracking-[0.3em] text-blue-400">
                                    RESEARCH
                                </p>
                            </div>
                        </Link>
                        <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
                            Your proxy to real market insights. We turn human
                            conversations into precision data — through CATI,
                            validation, and analysis.
                        </p>
                        <a
                            href="https://in.linkedin.com/company/proksy-research"
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 mt-5 text-blue-400 hover:text-blue-300 text-sm font-outfit"
                            data-testid="footer-linkedin-link"
                        >
                            <Linkedin className="w-4 h-4" />
                            Follow on LinkedIn
                            <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                    </div>

                    <div>
                        <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400 font-bold">
                            Company
                        </p>
                        <ul className="mt-5 space-y-3 text-sm text-slate-300">
                            <li>
                                <Link
                                    to="/about"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-about"
                                >
                                    About
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/services"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-services"
                                >
                                    Services
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/industries"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-industries"
                                >
                                    Industries
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-contact"
                                >
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400 font-bold">
                            Resources
                        </p>
                        <ul className="mt-5 space-y-3 text-sm text-slate-300">
                            <li>
                                <a
                                    href="#sample-report"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-sample"
                                >
                                    Sample Report
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#case-study"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-case-study"
                                >
                                    Case Study
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#process"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-process"
                                >
                                    Our Process
                                </a>
                            </li>
                            <li>
                                <a
                                    href="#privacy"
                                    className="hover:text-white transition"
                                    data-testid="footer-link-privacy"
                                >
                                    Privacy Policy
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400 font-bold">
                            Get in touch
                        </p>
                        <ul className="mt-5 space-y-3 text-sm text-slate-300">
                            <li className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-blue-400" />
                                <span data-testid="footer-email">
                                    hello@proksyresearch.com
                                </span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-blue-400" />
                                <span data-testid="footer-phone">
                                    +1 (302) 551-0884
                                </span>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                                <span data-testid="footer-address" className="leading-snug">
                                    2044 Limestone Rd
                                    <br />
                                    Wilmington, DE 19808
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="divider-glow my-10" />

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-slate-500 font-mono-pr">
                    <p data-testid="footer-copyright">
                        © {new Date().getFullYear()} Proksy Research — Your Proxy
                        to Real Market Insights.
                    </p>
                    <div className="flex items-center gap-4">
                        <span className="inline-flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            CATI Network · Online
                        </span>
                        <span>v1.0</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
